import { QueryParamConfig, useQueryParams } from 'use-query-params';
import { encodeVariableValue, VAR_QUERY_PARAM_PREFIX, VariableDefinition, VariableValue } from '../../../core';

export function getURLQueryParamName(name: string, namespace?: string) {
  if (namespace) {
    return `${namespace}.${VAR_QUERY_PARAM_PREFIX}${name}`;
  }
  return `${VAR_QUERY_PARAM_PREFIX}${name}`;
}

export function decodeVariableValue(value: string): VariableValue {
  if (!value) {
    return null;
  }
  // Split on comma (but not escaped comma) to support multi-value variables. Then unescape commas.
  const values = value.split(/(?<!\\),/).map((v) => v.replace(/\\,/g, ','));
  if (values.length === 1) {
    return values[0] as string;
  }
  return values;
}

const VariableValueParam: QueryParamConfig<VariableValue> = {
  encode: encodeVariableValue,
  decode: (v) => {
    if (typeof v === 'string') {
      return decodeVariableValue(v);
    }
    return '';
  },
};

export function useVariableQueryParams(defs: VariableDefinition[], namespace?: string) {
  const config: Record<string, typeof VariableValueParam> = {};
  defs.forEach((def) => {
    const name = getURLQueryParamName(def.spec.name, namespace);
    config[name] = VariableValueParam;
  });
  return useQueryParams(config, { updateType: 'replaceIn' });
}

export function getInitalValuesFromQueryParameters(
  queryParamValues: Record<string, VariableValue>,
  namespace?: string
): Record<string, VariableValue> {
  const values: Record<string, VariableValue> = {};

  Object.keys(queryParamValues).forEach((key) => {
    const value = queryParamValues[key];
    if (!value) {
      return;
    }

    if (namespace) {
      // When namespace is specified, only process parameters that match our namespace
      const expectedPrefix = `${namespace}.${VAR_QUERY_PARAM_PREFIX}`;
      if (key.startsWith(expectedPrefix)) {
        const name = key.replace(expectedPrefix, '');
        values[name] = value;
      }
    } else {
      // When no namespace is specified, process all var- parameters (including namespaced ones)
      if (key.startsWith(VAR_QUERY_PARAM_PREFIX)) {
        const name = key.replace(VAR_QUERY_PARAM_PREFIX, '');
        values[name] = value;
      } else if (key.includes(`.${VAR_QUERY_PARAM_PREFIX}`)) {
        // Include namespaced parameters as-is when no namespace filter is applied
        values[key] = value;
      }
    }
  });
  return values;
}
